import React, { useState, useEffect, useRef } from "react";
import {
    Input,
    InputGroup,
    InputRightElement,
    Button,
    IconButton,
    Text,
    FormControl,
    FormErrorMessage,
    useToast,
    CircularProgress,
    Center,
    Box,
    Image,
    Heading,
    Container,
    VStack,
    Flex,
    FormLabel,
} from "@chakra-ui/react";
import { ViewIcon, ViewOffIcon } from "@chakra-ui/icons";
import { useAuth } from "./providers/AuthProvider";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {supabase} from "./utils/supabaseClient";
import edthosLogo from "./Edthos.png";


// Color theme constants

const LoginPage = () => {
    const { login, auth } = useAuth();
    const [loginForm, setLoginForm] = useState({
        email: "",
        password: "",
    });
    const [errors, setErrors] = useState({});
    const [loginError, setLoginError] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [logoURL, setLogoURL] = useState(null);
    const [showPassword, setShowPassword] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();
    const emailInputRef = useRef(null);

    const validate = () => {
        const reg = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w\w+)+$/;
        setLoginError("");
        const nextErrors = {};
        if (loginForm.email === undefined || loginForm.email === "") {
            nextErrors.email = "Email is required";
        } else if (!reg.test(loginForm.email)) {
            nextErrors.email = "Must be a valid email address";
        }
        if (loginForm.password === undefined || loginForm.password === "") {
            nextErrors.password = "Password is required";
        }
        setErrors(nextErrors);
        return Object.keys(nextErrors).length === 0;
    };

    const loginUser = async (email, password) => {
        try {
            setLoading(true);
            const { error } = await login(email, password);
            if (error) {
                setLoginError(error.message);
            } else {
                toast({
                    title: "Login Successful",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });
            }
            setLoading(false);
        } catch (error) {
            setLoginError(error.message);
        }
    };

    useEffect(() => {
        if (auth) {
            navigate("/enrolled-courses");
        }
    }, [auth]);

    // Auto-focus email input on mount for better mobile UX
    useEffect(() => {
        // Small delay to ensure the input is rendered and ready
        const timer = setTimeout(() => {
            if (emailInputRef.current) {
                emailInputRef.current.focus();
            }
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const handleLogin = () => {
        if (validate()) {
            setLoginError("");
            loginUser(loginForm.email, loginForm.password);
        }
    };

    // Add this function to handle Enter key press
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            handleLogin();
        }
    };

    const getOrganisationLogo = async () => {
        console.log("getting organisation logo")
        const {data, error} = await supabase
            .from("organization")
            .select("logo_url")
            .single()
        console.log(data, "organisation logo")
        setLogoURL(data.logo_url)
    }

    useEffect(() => {
        getOrganisationLogo()
    }, [])

    return (
        <Flex
            minH="100vh"
            w="100%"
            direction="column"
            justifyContent="center"
            alignItems="center"
            px={{ base: 4, md: 0 }}
            py={8}
            onKeyDown={handleKeyDown}
            bgGradient="linear(to-br, gray.50, blue.50)"
        >
            {/* Organisation logo above the card */}
            {logoURL && (
                <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <Box mb={6}>
                        <Image
                            src={logoURL}
                            alt="Organisation Logo"
                            maxH={{ base: "56px", md: "64px" }}
                            maxW="240px"
                            objectFit="contain"
                        />
                    </Box>
                </motion.div>
            )}

            <Container maxW="md" w="100%" px={{ base: 0, md: 4 }}>
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.6, ease: "easeOut" }}
                >
                    <Box
                        bg="white"
                        borderRadius="xl"
                        boxShadow="xl"
                        border="1px solid"
                        borderColor="gray.100"
                        overflow="hidden"
                        w="100%"
                    >
                        {/* Card header */}
                        <Box px={{ base: 6, md: 8 }} pt={{ base: 6, md: 8 }} pb={4} textAlign="center">
                            <Heading size="lg" color="gray.800" mb={1}>
                                Sign In
                            </Heading>
                            <Text fontSize="sm" color="gray.500">
                                Enter your credentials to continue
                            </Text>
                        </Box>

                        {/* Card body */}
                        <Box px={{ base: 6, md: 8 }} pb={{ base: 6, md: 8 }}>
                            <FormControl
                                isRequired
                                isInvalid={"email" in errors || "password" in errors}
                            >
                                <VStack spacing={{ base: 5, md: 4 }} align="stretch">
                                    <Box>
                                        <FormLabel
                                            fontSize="sm"
                                            mb={1.5}
                                            color="gray.700"
                                            fontWeight="medium"
                                        >
                                            Email
                                        </FormLabel>
                                        <Input
                                            ref={emailInputRef}
                                            id="email"
                                            type="email"
                                            placeholder="Enter your email"
                                            value={loginForm.email}
                                            onChange={(e) => {
                                                setLoginForm({ ...loginForm, email: e.target.value });
                                                if (loginError) setLoginError("");
                                                if (errors.email) setErrors((prev) => ({ ...prev, email: undefined }));
                                            }}
                                            bg="white"
                                            size={{ base: "md", md: "lg" }}
                                            fontSize={{ base: "16px", md: "md" }}
                                            minH={{ base: "48px", md: "40px" }}
                                            autoComplete="email"
                                            autoCapitalize="none"
                                            autoCorrect="off"
                                            inputMode="email"
                                            borderColor="gray.200"
                                            _hover={{ borderColor: "gray.300" }}
                                            _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                                        />
                                        {"email" in errors && (
                                            <FormErrorMessage fontSize="xs" mt={1}>
                                                {errors.email}
                                            </FormErrorMessage>
                                        )}
                                    </Box>

                                    <Box>
                                        <Flex justify="space-between" align="center" mb={1.5}>
                                            <FormLabel
                                                fontSize="sm"
                                                mb={0}
                                                color="gray.700"
                                                fontWeight="medium"
                                            >
                                                Password
                                            </FormLabel>
                                            <Link
                                                to="/forgot-password"
                                                style={{
                                                    fontSize: "0.8rem",
                                                    color: "#3182ce",
                                                    textDecoration: "none",
                                                }}
                                                onMouseEnter={e => e.target.style.textDecoration = "underline"}
                                                onMouseLeave={e => e.target.style.textDecoration = "none"}
                                            >
                                                Forgot password?
                                            </Link>
                                        </Flex>
                                        <InputGroup size={{ base: "md", md: "lg" }}>
                                            <Input
                                                id="password"
                                                type={showPassword ? "text" : "password"}
                                                placeholder="Enter your password"
                                                value={loginForm.password}
                                                onChange={(e) => {
                                                    setLoginForm({ ...loginForm, password: e.target.value });
                                                    if (loginError) setLoginError("");
                                                    if (errors.password) setErrors((prev) => ({ ...prev, password: undefined }));
                                                }}
                                                bg="white"
                                                pr="12"
                                                fontSize={{ base: "16px", md: "md" }}
                                                minH={{ base: "48px", md: "40px" }}
                                                autoComplete="current-password"
                                                onKeyDown={handleKeyDown}
                                                borderColor="gray.200"
                                                _hover={{ borderColor: "gray.300" }}
                                                _focus={{ borderColor: "blue.400", boxShadow: "0 0 0 1px var(--chakra-colors-blue-400)" }}
                                            />
                                            <InputRightElement height={{ base: "48px", md: "40px" }} width="12">
                                                <IconButton
                                                    variant="ghost"
                                                    size="sm"
                                                    aria-label={showPassword ? "Hide password" : "Show password"}
                                                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                                                    onClick={() => setShowPassword((prev) => !prev)}
                                                    color="gray.400"
                                                    _hover={{ bg: "gray.100", color: "gray.600" }}
                                                />
                                            </InputRightElement>
                                        </InputGroup>
                                        {"password" in errors && (
                                            <FormErrorMessage fontSize="xs" mt={1}>
                                                {errors.password}
                                            </FormErrorMessage>
                                        )}
                                    </Box>

                                    {loginError && (
                                        <Text
                                            color="red.500"
                                            fontSize={{ base: "xs", md: "sm" }}
                                            textAlign="left"
                                            px={1}
                                        >
                                            {loginError}
                                        </Text>
                                    )}

                                    {isLoading ? (
                                        <Center py={2}>
                                            <CircularProgress
                                                isIndeterminate
                                                size={{ base: "30px", md: "35px" }}
                                                color="blue.500"
                                            />
                                        </Center>
                                    ) : (
                                        <Button
                                            onClick={handleLogin}
                                            mt={{ base: 2, md: 3 }}
                                            width="full"
                                            height={{ base: "48px", md: "44px" }}
                                            color="white"
                                            bg="gray.800"
                                            _hover={{ bg: "gray.700" }}
                                            _active={{ bg: "gray.900" }}
                                            fontSize={{ base: "md", md: "md" }}
                                            fontWeight="semibold"
                                            borderRadius="lg"
                                        >
                                            Sign In
                                        </Button>
                                    )}

                                    <Text fontSize="xs" color="gray.500" textAlign="center" pt={1}>
                                        Contact your administrator if you need an account.
                                    </Text>
                                </VStack>
                            </FormControl>
                        </Box>
                    </Box>
                </motion.div>
            </Container>

            {/* Powered by Edthos footer */}
            <Flex mt={8} align="center" gap={1.5}>
                <Text fontSize="xs" color="gray.400">Powered by</Text>
                <Image
                    src={edthosLogo}
                    alt="Edthos"
                    h="14px"
                    w="auto"
                    objectFit="contain"
                    opacity={0.4}
                />
            </Flex>
        </Flex>
    );
};

export default LoginPage;
